// Meus alertas (PO1-2194, A8): o que o usuário recebeu e em quais alertas do
// produto ele está inscrito. Uma instância por produto (prop `produto`, montada
// por makeAlertasItens em routes/products.js).
//
// Sem `?.`/`??`: o build roda em CRA 3.4 / Node 12.

import React, { useCallback, useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { Badge, Button, ButtonGroup, Input, Spinner, Table } from "reactstrap";

import PageHero, { PageHeroRefreshButton } from "../../../components/PageHero";
import ShellCard, { TableSurface } from "../../../components/ShellCard";
import { AUTOPILOT_COLORS } from "../../../components/autopilot/styles";
import { useToast } from "../../../hooks/toast";
import {
  enviarTeste,
  excluirMinha,
  listarMinhas,
  listarMinhasInscricoes,
  listarTarefas,
  marcarLido,
  marcarTodosLidos,
  removerMinhaInscricao,
  salvarMinhaInscricao,
} from "../../../services/alertasApi";
import {
  FREQUENCIA_CANAL_LABEL,
  PRODUTO_LABEL,
  abrirLink,
  ehRelatorio,
  formatarDataHora,
  inscricaoPadrao,
  inscricaoRelatorio,
  mensagemErro,
  normalizarInscricao,
  opcoesFrequenciaCanal,
  opcoesFrequenciaTarefa,
  rotaTarefas,
  tempoRelativo,
} from "../alertasUtils";
import "./styles.css";

const POR_PAGINA = 20;

const mutedStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.8rem" };
const titleStyle = { color: AUTOPILOT_COLORS.title, fontWeight: 600 };
const tabsStyle = { display: "flex", gap: "0.5rem", flexWrap: "wrap", marginTop: "1.25rem" };

function Abas({ valor, opcoes, onChange }) {
  return (
    <ButtonGroup>
      {opcoes.map((o) => (
        <Button
          key={o.value}
          size="sm"
          color="info"
          outline={valor !== o.value}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </Button>
      ))}
    </ButtonGroup>
  );
}

// ─── Aba Recebidos ──────────────────────────────────────────────────────────

function Recebidos({ produto, flash, recarregarToken }) {
  const history = useHistory();
  const [status, setStatus] = useState("todos");
  const [pagina, setPagina] = useState(1);
  const [dados, setDados] = useState({ items: [], total: 0, naoLidos: 0 });
  const [carregando, setCarregando] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const data = await listarMinhas({ produto, status, limite: POR_PAGINA, pagina });
      setDados({
        items: (data && data.items) || [],
        total: (data && Number(data.total)) || 0,
        naoLidos: (data && Number(data.naoLidos)) || 0,
      });
    } catch (e) {
      flash("danger", mensagemErro(e, "Falha ao carregar os alertas recebidos."));
    } finally {
      setCarregando(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [produto, status, pagina]);

  useEffect(() => {
    carregar();
  }, [carregar, recarregarToken]);

  const trocarStatus = (v) => {
    setStatus(v);
    setPagina(1);
  };

  const ler = async (item) => {
    try {
      await marcarLido(item.id);
      carregar();
    } catch (e) {
      flash("danger", mensagemErro(e, "Falha ao marcar como lido."));
    }
  };

  const excluir = async (item) => {
    try {
      await excluirMinha(item.id);
      flash("success", "Alerta excluído.");
      carregar();
    } catch (e) {
      flash("danger", mensagemErro(e, "Falha ao excluir o alerta."));
    }
  };

  const lerTodos = async () => {
    try {
      await marcarTodosLidos(produto);
      carregar();
    } catch (e) {
      flash("danger", mensagemErro(e, "Falha ao marcar todos como lidos."));
    }
  };

  const abrir = async (item) => {
    if (!item.lido) {
      try {
        await marcarLido(item.id);
      } catch (e) {
        // segue para o link
      }
    }
    abrirLink(item.link, history);
  };

  const totalPaginas = Math.max(1, Math.ceil(dados.total / POR_PAGINA));

  return (
    <ShellCard
      title="Recebidos"
      description={`${dados.total} no total · ${dados.naoLidos} não lidos`}
      action={
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", justifyContent: "flex-end" }}>
          <Abas
            valor={status}
            onChange={trocarStatus}
            opcoes={[
              { value: "todos", label: "Todos" },
              { value: "naoLidos", label: "Não lidos" },
            ]}
          />
          <Button size="sm" color="secondary" outline onClick={lerTodos} disabled={dados.naoLidos === 0}>
            Marcar todos como lidos
          </Button>
        </div>
      }
    >
      <TableSurface>
        <div className="table-responsive">
          <Table className="text-left hub-thead-accent" style={{ color: AUTOPILOT_COLORS.text }}>
            <thead className="text-primary">
              <tr>
                <th>Alerta</th>
                <th>Quando</th>
                <th className="text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {dados.items.map((item) => (
                <tr
                  key={item.id}
                  style={{
                    background: item.lido ? "transparent" : AUTOPILOT_COLORS.insetSurfaceActive,
                  }}
                >
                  <td style={{ cursor: item.link ? "pointer" : "default" }} onClick={() => abrir(item)}>
                    <div style={titleStyle}>
                      {!item.lido ? (
                        <span
                          aria-label="Não lido"
                          style={{
                            display: "inline-block",
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: AUTOPILOT_COLORS.statusInfo,
                            marginRight: 8,
                          }}
                        />
                      ) : null}
                      {item.nome}
                      {item.tipo === "TAREFA" ? (
                        <Badge color="warning" pill style={{ marginLeft: 8 }}>
                          Tarefa
                        </Badge>
                      ) : null}
                    </div>
                    <div style={{ color: AUTOPILOT_COLORS.textSoft, fontSize: "0.84rem" }}>{item.mensagem}</div>
                  </td>
                  <td style={{ whiteSpace: "nowrap" }} title={formatarDataHora(item.criadoEm)}>
                    <span style={mutedStyle}>{tempoRelativo(item.criadoEm)}</span>
                  </td>
                  <td className="text-right" style={{ whiteSpace: "nowrap" }}>
                    {!item.lido ? (
                      <Button size="sm" color="link" title="Marcar como lido" onClick={() => ler(item)}>
                        <i className="tim-icons icon-check-2" />
                      </Button>
                    ) : null}
                    <Button size="sm" color="link" title="Excluir" onClick={() => excluir(item)}>
                      <i className="tim-icons icon-trash-simple" />
                    </Button>
                  </td>
                </tr>
              ))}
              {dados.items.length === 0 && !carregando ? (
                <tr>
                  <td colSpan={3} style={{ ...mutedStyle, padding: "1.25rem" }}>
                    {status === "naoLidos" ? "Nenhum alerta não lido." : "Nenhum alerta por aqui."}
                  </td>
                </tr>
              ) : null}
              {carregando && dados.items.length === 0 ? (
                <tr>
                  <td colSpan={3} style={{ padding: "1.25rem", textAlign: "center" }}>
                    <Spinner size="sm" />
                  </td>
                </tr>
              ) : null}
            </tbody>
          </Table>
        </div>
      </TableSurface>
      {totalPaginas > 1 ? (
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "0.75rem", marginTop: "0.75rem" }}>
          <Button size="sm" color="secondary" outline disabled={pagina <= 1 || carregando} onClick={() => setPagina(pagina - 1)}>
            Anterior
          </Button>
          <span style={mutedStyle}>
            Página {pagina} de {totalPaginas}
          </span>
          <Button
            size="sm"
            color="secondary"
            outline
            disabled={pagina >= totalPaginas || carregando}
            onClick={() => setPagina(pagina + 1)}
          >
            Próxima
          </Button>
        </div>
      ) : null}
    </ShellCard>
  );
}

// ─── Aba Inscrições ─────────────────────────────────────────────────────────
// PO1-3389: avisos e tarefas separados. Aviso: Sino / E-mail com Desligado (os
// dois desligados = sem inscrição). Tarefa: chega sempre ao responsável; aqui só
// se escolhe a frequência de cada canal (sem Desligado).

function Inscricoes({ produto, flash }) {
  const [itens, setItens] = useState([]);
  const [abertas, setAbertas] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState({});

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const data = await listarMinhasInscricoes(produto);
      setItens((data && data.items) || []);
    } catch (e) {
      flash("danger", mensagemErro(e, "Falha ao carregar as inscrições."));
    } finally {
      setCarregando(false);
    }
    try {
      const t = await listarTarefas({ produto, status: "ABERTA" });
      setAbertas((t && t.items) || []);
    } catch (e) {
      setAbertas([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [produto]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const atualizarLocal = (idAlerta, inscricao) =>
    setItens((prev) => prev.map((i) => (i.idAlerta === idAlerta ? { ...i, inscricao } : i)));

  const salvar = async (alerta, inscricao) => {
    const id = alerta.idAlerta;
    setSalvando((p) => ({ ...p, [id]: true }));
    try {
      if (inscricao) {
        const data = await salvarMinhaInscricao(id, inscricao);
        atualizarLocal(id, (data && data.inscricao) || inscricao);
        flash("success", `"${alerta.nome}" salvo.`);
      } else {
        await removerMinhaInscricao(id);
        atualizarLocal(id, null);
        flash("success", `"${alerta.nome}" desligado.`);
      }
    } catch (e) {
      flash("danger", mensagemErro(e, "Falha ao salvar a inscrição."));
    } finally {
      setSalvando((p) => ({ ...p, [id]: false }));
    }
  };

  // Aviso: parte da inscrição atual (ou nada); desligar os dois canais remove.
  const trocarCanalAviso = (alerta, canal, valor) => {
    const atual = normalizarInscricao(alerta.inscricao);
    const nova = { sino: atual.sino, email: atual.email };
    nova[canal] = valor || null;
    salvar(alerta, nova.sino || nova.email ? nova : null);
  };

  // Tarefa: parte da preferência (ou do padrão) e sempre manda os dois canais.
  const trocarCanalTarefa = (alerta, canal, valor) => {
    const atual = normalizarInscricao(alerta.inscricao || inscricaoPadrao(alerta));
    const nova = { sino: atual.sino, email: atual.email };
    nova[canal] = valor;
    salvar(alerta, nova);
  };

  const selectAviso = (alerta, canal) => {
    const opcoes = opcoesFrequenciaCanal(alerta, canal);
    const atual = normalizarInscricao(alerta.inscricao);
    return (
      <Input
        type="select"
        bsSize="sm"
        value={atual[canal] || ""}
        disabled={!!salvando[alerta.idAlerta] || opcoes.length <= 1}
        aria-label={`${canal === "sino" ? "Sino" : "E-mail"} de ${alerta.nome}`}
        onChange={(e) => {
          const v = e.target.value;
          trocarCanalAviso(alerta, canal, v);
        }}
      >
        {opcoes.map((f) => (
          <option key={f || "off"} value={f}>
            {FREQUENCIA_CANAL_LABEL[f] || f}
          </option>
        ))}
      </Input>
    );
  };

  const selectTarefa = (alerta, canal) => {
    const opcoes = opcoesFrequenciaTarefa(alerta, canal);
    if (!opcoes.length) return <span style={mutedStyle}>Não se aplica</span>;
    const atual = normalizarInscricao(alerta.inscricao || inscricaoPadrao(alerta));
    return (
      <Input
        type="select"
        bsSize="sm"
        value={atual[canal] || opcoes[0]}
        disabled={!!salvando[alerta.idAlerta] || opcoes.length <= 1}
        aria-label={`${canal === "sino" ? "Sino" : "E-mail"} de ${alerta.nome}`}
        onChange={(e) => {
          const v = e.target.value;
          trocarCanalTarefa(alerta, canal, v);
        }}
      >
        {opcoes.map((f) => (
          <option key={f} value={f}>
            {FREQUENCIA_CANAL_LABEL[f] || f}
          </option>
        ))}
      </Input>
    );
  };

  const nomeComSelo = (alerta) => (
    <div style={titleStyle}>
      {alerta.nome}
      {!alerta.produto ? (
        <Badge color="secondary" pill style={{ marginLeft: 8 }}>
          Todos os produtos
        </Badge>
      ) : null}
    </div>
  );

  // PO1-3384: relatórios ficam num card próprio, só com liga/desliga do e-mail.
  const avisos = itens.filter((a) => a.tipo !== "TAREFA" && !ehRelatorio(a));
  const tarefas = itens.filter((a) => a.tipo === "TAREFA");
  const relatorios = itens.filter((a) => ehRelatorio(a));

  const selectRelatorio = (alerta) => {
    const ligado = !!normalizarInscricao(alerta.inscricao).email;
    return (
      <Input
        type="select"
        bsSize="sm"
        value={ligado ? "on" : ""}
        disabled={!!salvando[alerta.idAlerta]}
        aria-label={`E-mail de ${alerta.nome}`}
        onChange={(e) => {
          const v = e.target.value;
          salvar(alerta, v ? inscricaoRelatorio(alerta) : null);
        }}
      >
        <option value="">Desligado</option>
        <option value="on">Ligado</option>
      </Input>
    );
  };
  const linhaVazia = (texto) => (
    <tr>
      <td colSpan={3} style={{ ...mutedStyle, padding: "1.25rem" }}>
        {carregando ? <Spinner size="sm" /> : texto}
      </td>
    </tr>
  );
  const cabecalho = (primeira) => (
    <thead className="text-primary">
      <tr>
        <th>{primeira}</th>
        <th style={{ width: 170 }}>Sino</th>
        <th style={{ width: 170 }}>E-mail</th>
      </tr>
    </thead>
  );
  const rotaMinhasTarefas = rotaTarefas(produto);

  return (
    <>
      <ShellCard title="Avisos" description="Você recebe só os que ligar. Para cada canal, escolha se chega na hora ou no resumo diário.">
        <TableSurface>
          <div className="table-responsive">
            <Table className="text-left hub-thead-accent" style={{ color: AUTOPILOT_COLORS.text }}>
              {cabecalho("Aviso")}
              <tbody>
                {avisos.map((alerta) => (
                  <tr key={alerta.idAlerta}>
                    <td>
                      {nomeComSelo(alerta)}
                      <div style={mutedStyle}>{alerta.descricao}</div>
                    </td>
                    <td>{selectAviso(alerta, "sino")}</td>
                    <td>{selectAviso(alerta, "email")}</td>
                  </tr>
                ))}
                {avisos.length === 0 ? linhaVazia("Nenhum aviso disponível para este produto.") : null}
              </tbody>
            </Table>
          </div>
        </TableSurface>
      </ShellCard>
      {relatorios.length ? (
        <ShellCard
          title="Relatórios"
          description="Chegam por e-mail, uma vez por dia útil, para o embarcador selecionado, com a planilha anexa. Sem sino."
        >
          <TableSurface>
            <div className="table-responsive">
              <Table className="text-left hub-thead-accent" style={{ color: AUTOPILOT_COLORS.text }}>
                <thead className="text-primary">
                  <tr>
                    <th>Relatório</th>
                    <th style={{ width: 170 }}>E-mail</th>
                  </tr>
                </thead>
                <tbody>
                  {relatorios.map((alerta) => (
                    <tr key={alerta.idAlerta}>
                      <td>
                        {nomeComSelo(alerta)}
                        <div style={mutedStyle}>{alerta.descricao}</div>
                      </td>
                      <td>{selectRelatorio(alerta)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </TableSurface>
        </ShellCard>
      ) : null}
      {tarefas.length ? (
        <ShellCard
          title="Tarefas"
          description="Chegam sempre que você for escolhido como responsável. Não dá para desligar; aqui você escolhe só como chegam."
        >
          <TableSurface>
            <div className="table-responsive">
              <Table className="text-left hub-thead-accent" style={{ color: AUTOPILOT_COLORS.text }}>
                {cabecalho("Tarefa")}
                <tbody>
                  {tarefas.map((alerta) => (
                    <tr key={alerta.idAlerta}>
                      <td>
                        {nomeComSelo(alerta)}
                        <div style={mutedStyle}>{alerta.descricao}</div>
                      </td>
                      <td>{selectTarefa(alerta, "sino")}</td>
                      <td>{selectTarefa(alerta, "email")}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </TableSurface>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginTop: 10, fontSize: "0.85rem" }}>
            <span style={mutedStyle}>Abertas agora:</span>
            {abertas.length === 0 ? <span style={mutedStyle}>nenhuma</span> : null}
            {abertas.map((t) => (
              <span key={t.id}>
                {t.link ? <Link to={t.link}>{t.mensagem || t.nome}</Link> : t.mensagem || t.nome}
              </span>
            ))}
            {rotaMinhasTarefas ? (
              <Link to={rotaMinhasTarefas} style={{ marginLeft: "auto" }}>
                Ver em Minhas tarefas
              </Link>
            ) : null}
          </div>
        </ShellCard>
      ) : null}
    </>
  );
}

// ─── Tela ───────────────────────────────────────────────────────────────────

export default function MeusAlertas({ produto }) {
  const { addToast } = useToast();
  const [aba, setAba] = useState("recebidos");
  const [enviando, setEnviando] = useState(null);
  const [recarregarToken, setRecarregarToken] = useState(0);

  const flash = useCallback(
    (type, description) =>
      addToast({ place: "tr", type, title: type === "success" ? "OK" : "Atenção", description }),
    [addToast],
  );

  const testar = async (tarefa) => {
    setEnviando(tarefa ? "tarefa" : "alerta");
    try {
      const data = (await enviarTeste({ tarefa, produto })) || {};
      const gravados = Number(data.gravados) || 0;
      const deduplicados = Number(data.deduplicados) || 0;
      if (deduplicados > 0 && gravados === 0) {
        flash("warning", "Você já recebeu este teste nas últimas 24 horas.");
      } else {
        const emails = Number(data.emails) || 0;
        flash(
          "success",
          `${tarefa ? "Tarefa" : "Alerta"} de teste enviado${emails > 0 ? ` (${emails} e-mail${emails > 1 ? "s" : ""})` : ""}.`,
        );
        setRecarregarToken((t) => t + 1);
      }
    } catch (e) {
      const status = e && e.response && e.response.status;
      if (status === 422) {
        flash("warning", mensagemErro(e, "Inscreva-se no alerta de teste primeiro"));
        if (!tarefa) setAba("inscricoes");
      } else {
        flash("danger", mensagemErro(e, "Falha ao enviar o teste."));
      }
    } finally {
      setEnviando(null);
    }
  };

  return (
    <div className="content meus-alertas">
      <PageHero
        eyebrow={PRODUTO_LABEL[produto] || "Alertas"}
        title="Meus alertas"
        description="O que chegou para você e em quais alertas você está inscrito, com canal e frequência de cada um."
        actions={
          <>
            <PageHeroRefreshButton onClick={() => setRecarregarToken((t) => t + 1)} />
            <Button size="sm" color="info" outline disabled={!!enviando} onClick={() => testar(false)}>
              {enviando === "alerta" ? <Spinner size="sm" /> : "Enviar alerta de teste"}
            </Button>
            <Button size="sm" color="info" outline disabled={!!enviando} onClick={() => testar(true)}>
              {enviando === "tarefa" ? <Spinner size="sm" /> : "Enviar tarefa de teste"}
            </Button>
          </>
        }
      />

      <div style={tabsStyle}>
        <Abas
          valor={aba}
          onChange={setAba}
          opcoes={[
            { value: "recebidos", label: "Recebidos" },
            { value: "inscricoes", label: "Inscrições" },
          ]}
        />
      </div>

      {aba === "recebidos" ? (
        <Recebidos produto={produto} flash={flash} recarregarToken={recarregarToken} />
      ) : (
        <Inscricoes key={`ins-${recarregarToken}`} produto={produto} flash={flash} />
      )}
    </div>
  );
}
